import { computed } from 'vue'

/**
 * Frontend DISPLAY masking for identifying IDs (household / registration /
 * sleeve ids rendered as "#1135"), gated on the caller's "Masked" role.
 *
 * Why this is client-side while names/account-numbers are masked server-side:
 * a name is pure display, so the backend never sends the real value (a hard
 * boundary). An id, by contrast, is the FUNCTIONAL key the SPA needs for
 * navigation + detail fetches — it must stay real in the payload — so only its
 * on-screen rendering is masked here. Advisor / representative ids are NOT
 * masked (explicitly out of scope).
 *
 * Mask style mirrors the backend full-mask: every character → '*', length
 * preserved (same as Masking.Mask(x, maskAll:true) server-side).
 */
export function useMasking() {
  const { hasRole } = useAuth()
  const isMasked = computed(() => hasRole('Masked'))

  /** Render an id for display — fully masked to '*' (length kept) when masked. */
  function maskId(id: number | string | null | undefined): string {
    if (id === null || id === undefined || id === '') return ''
    const s = String(id)
    return isMasked.value ? '*'.repeat(s.length) : s
  }

  /** "#1135" → "#****" when masked; '' for a null/empty id. */
  function hashId(id: number | string | null | undefined): string {
    if (id === null || id === undefined || id === '') return ''
    return `#${maskId(id)}`
  }

  /**
   * Mask an account / registration NUMBER for DISPLAY (full mask, length
   * kept — mirrors the backend `Masking.Mask(x, maskAll:true)`). Use this
   * where the real value must stay usable as a key (e.g. the documents
   * account filter) but shouldn't be shown to a masked user.
   */
  function maskAccount(value: string | null | undefined): string {
    if (value === null || value === undefined || value === '') return ''
    const s = String(value)
    return isMasked.value ? '*'.repeat(s.length) : s
  }

  return { isMasked, maskId, hashId, maskAccount }
}
